import { Box, Button, HStack } from "@chakra-ui/react"
import {
  AUGMENTATION_PANEL_ACTION_BUTTON_PROPS,
  AUGMENTATION_PANEL_SUBMIT_BUTTON_PROPS
} from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import ConfirmOverlay from "@pathwright/ux/src/components/confirm/ConfirmOverlay"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import { FormikProps } from "formik"
import {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useClickAway } from "react-use"
import RichTextEditorWithMediaManager from "../../media-manager/RichTextEditorWithMediaManager"
import { useCoverPageIsEditable } from "../access"
import { useCoverPageContext } from "../CoverPageProvider"
import { CoverPagePanelSectionKey } from "../types"
import {
  COVER_PAGE_INLINE_EDITOR_EDITING_STYLES,
  COVER_PAGE_INLINE_EDITOR_FIELD_STYLES,
  COVER_PAGE_INLINE_EDITOR_SOFT_STYLES_SPACIOUS,
  COVER_PAGE_REVEAL_IN_SECTION,
  coverPageRevealOnHoverCss
} from "../ui"
import { useCoverPageTranslate } from "../utils"
import { CoverPageSectionEditTriggerButton } from "./CoverPageSectionTrigger"

const COVER_PAGE_INLINE_SECTION_EDIT_REQUEST_EVENT =
  "cover-page:inline-section-edit-request"

type CoverPageInlineSectionEditRequestDetail = {
  sectionKey: CoverPagePanelSectionKey
}

export function requestCoverPageInlineSectionEdit(
  sectionKey: CoverPagePanelSectionKey
) {
  if (typeof window === "undefined") return

  window.dispatchEvent(
    new CustomEvent<CoverPageInlineSectionEditRequestDetail>(
      COVER_PAGE_INLINE_SECTION_EDIT_REQUEST_EVENT,
      {
        detail: { sectionKey }
      }
    )
  )
}

export type UseCoverPageInlineSectionEditorArgs = {
  sectionKey: CoverPagePanelSectionKey
}

export type CoverPageInlineSectionEditorState = ReturnType<
  typeof useCoverPageInlineSectionEditor
>

export function useCoverPageInlineSectionEditor({
  sectionKey
}: UseCoverPageInlineSectionEditorArgs) {
  const resourceId = useCoverPageContext((ctx) => ctx.resourceId)
  const [isSectionEditing, setIsSectionEditing] = useState(false)
  const [editRequestCount, setEditRequestCount] = useState(0)

  const startEditing = useCallback(() => {
    setIsSectionEditing(true)
    setEditRequestCount((count) => count + 1)
  }, [])

  const finishEditing = useCallback(() => {
    setIsSectionEditing(false)
  }, [])

  // Navigating to another cover page exits local edit mode. Draft persistence is
  // keyed by resource, so unmounting or changing resources does not lose edits.
  useEffect(() => {
    setIsSectionEditing(false)
  }, [resourceId])

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleEditRequest = (event: Event) => {
      const detail = (
        event as CustomEvent<CoverPageInlineSectionEditRequestDetail>
      ).detail

      if (detail?.sectionKey === sectionKey) {
        startEditing()
      }
    }

    window.addEventListener(
      COVER_PAGE_INLINE_SECTION_EDIT_REQUEST_EVENT,
      handleEditRequest
    )

    return () => {
      window.removeEventListener(
        COVER_PAGE_INLINE_SECTION_EDIT_REQUEST_EVENT,
        handleEditRequest
      )
    }
  }, [sectionKey, startEditing])

  return {
    sectionKey,
    isSectionEditing,
    editRequestCount,
    startEditing,
    finishEditing
  }
}

type CoverPageInlineSectionEditorValues = {
  value: string
}

type CoverPageInlineSectionEditorProps = {
  sectionKey: CoverPagePanelSectionKey
  value: string
  triggerLabel: string
  onSave: (draft: string) => Promise<void>
  blankSlate?: ReactNode
  /** Shown in the empty editor, in place of the blank slate's hint. */
  placeholder?: string
}

// Clicks in these belong to the editor even though they render outside it:
// menus, popovers, dialogs, and the media manager.
const EDITOR_OVERLAY_SELECTOR =
  "[data-scope='popover'], [data-scope='menu'], [data-scope='dialog'], [data-scope='hover-card'], [data-scope='tooltip'], [data-scope='select'], [role='dialog'], [role='menu'], [role='listbox'], .tippy-box"

/**
 * The field around the inline editor. There is no Discard button: clicking
 * outside, or pressing Escape, closes an untouched editor and asks before
 * discarding a draft. Escape on that question goes back to the draft.
 */
function InlineEditorField({
  isEditing,
  editRequestCount,
  isDirty,
  isSubmitting,
  onDiscard,
  onKeyDown,
  children
}: {
  isEditing: boolean
  editRequestCount: number
  isDirty: boolean
  isSubmitting: boolean
  /** `fromKeyboard` asks the caller to put focus back where editing began. */
  onDiscard: (fromKeyboard: boolean) => void
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
  children: ReactNode
}) {
  const { t } = useCoverPageTranslate()
  const ref = useRef<HTMLDivElement>(null)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  // Whether the pending discard was started from the keyboard.
  const fromKeyboard = useRef(false)

  useEffect(() => {
    setConfirmDiscard(false)
  }, [isEditing, editRequestCount])

  useEffect(() => {
    // Focus after the confirmation unmounts and releases its trap. A repeated
    // request from Content also runs this effect for an already open editor.
    if (!isEditing || confirmDiscard) return
    const frame = requestAnimationFrame(() =>
      ref.current
        ?.querySelector<HTMLElement>('[contenteditable="true"]')
        ?.focus()
    )
    return () => cancelAnimationFrame(frame)
  }, [isEditing, editRequestCount, confirmDiscard])

  const leave = (viaKeyboard: boolean) => {
    fromKeyboard.current = viaKeyboard
    if (isDirty) setConfirmDiscard(true)
    else onDiscard(viaKeyboard)
  }

  const backToDraft = () => {
    setConfirmDiscard(false)
  }

  // Escape leaves the field: the keyboard's "click outside". The Cover Page is
  // a dialog that closes on Escape from a capture listener on the document,
  // which runs before any handler on this element, so claim the key first, on
  // the window.
  const onEscape = useRef<() => void>(() => {})
  onEscape.current = () => (confirmDiscard ? backToDraft() : leave(true))
  useEffect(() => {
    if (!isEditing || isSubmitting) return
    const claimEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing) return
      if (
        !(event.target instanceof Node) ||
        !ref.current?.contains(event.target)
      )
        return
      event.preventDefault()
      event.stopImmediatePropagation()
      onEscape.current()
    }
    window.addEventListener("keydown", claimEscape, true)
    return () => window.removeEventListener("keydown", claimEscape, true)
  }, [isEditing, isSubmitting])

  useClickAway(ref, (event) => {
    if (!isEditing || isSubmitting) return
    // A click in a menu or dialog the editor opened is not a click away. The
    // Cover Page is itself a dialog, so only count overlays that do not
    // contain this field.
    const overlay =
      event.target instanceof Element
        ? event.target.closest(EDITOR_OVERLAY_SELECTOR)
        : null
    if (overlay && !overlay.contains(ref.current)) return
    leave(false)
  })

  return (
    <Box
      ref={ref}
      position="relative"
      css={isEditing ? COVER_PAGE_INLINE_EDITOR_FIELD_STYLES : undefined}
      onKeyDown={(event) => {
        // Prevent Cmd/Ctrl+Enter from submitting the underlying form.
        if (
          confirmDiscard &&
          event.key === "Enter" &&
          (event.metaKey || event.ctrlKey)
        ) {
          event.preventDefault()
          event.stopPropagation()
          return
        }
        onKeyDown(event)
      }}
    >
      {children}
      {confirmDiscard ? (
        <ConfirmOverlay
          message={t("inline_section_editor.discard_prompt", {
            defaultValue: "Discard your changes?"
          })}
          confirmLabel={t("inline_section_editor.discard_confirm", {
            defaultValue: "Yes, discard"
          })}
          cancelLabel={t("inline_section_editor.discard_cancel", {
            defaultValue: "No, go back"
          })}
          onConfirm={() => onDiscard(fromKeyboard.current)}
          onCancel={backToDraft}
        />
      ) : null}
    </Box>
  )
}

function CoverPageInlineSectionEditor({
  sectionKey,
  value,
  triggerLabel,
  onSave,
  blankSlate,
  placeholder
}: CoverPageInlineSectionEditorProps) {
  const { t } = useCoverPageTranslate()
  const isEditable = useCoverPageIsEditable()
  const resourceId = useCoverPageContext((v) => v.resourceId)
  const editor = useCoverPageInlineSectionEditor({
    sectionKey
  })
  const initialValues = useMemo<CoverPageInlineSectionEditorValues>(
    () => ({ value }),
    [value]
  )
  const mediaManagerContextKey = useMemo(
    () => `resource-${resourceId}`,
    [resourceId]
  )

  const containerRef = useRef<HTMLDivElement>(null)
  // Set when Apply is triggered from the keyboard, so focus can follow.
  const keyboardSubmit = useRef(false)
  // The editor unmounts when editing ends, which would drop keyboard focus
  // to the page. Put it on what replaces the editor: the Placeholder, or
  // the section's edit control.
  const restoreFocus = useCallback(() => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        containerRef.current
          ?.querySelector<HTMLElement>(
            "[data-cover-blank-slate], .CoverPageSectionTriggerLink"
          )
          ?.focus()
      )
    )
  }, [])

  if (!resourceId || !editor) return null

  return (
    <FormikWithDiscardFormState
      key={`${sectionKey}:${resourceId}`}
      formStateKey={["cover-page", "inline-section", sectionKey, resourceId]}
      initialValues={initialValues}
      enableReinitialize
      formStateOnSubmit="preserve"
      onSubmit={async (values: CoverPageInlineSectionEditorValues) => {
        if (!isEditable)
          throw new Error("You do not have permission to edit this Cover Page.")
        try {
          if (values.value !== value) {
            await onSave(values.value)
          }
          editor.finishEditing()
          if (keyboardSubmit.current) restoreFocus()
        } catch (error) {
          toaster.create({
            title: t("inline_section_editor.save_error", {
              defaultValue: "Unable to save your changes. Please try again."
            }),
            type: "error"
          })
          throw error
        } finally {
          keyboardSubmit.current = false
        }
      }}
    >
      {(form: FormikProps<CoverPageInlineSectionEditorValues>) => (
        <Box ref={containerRef} position="relative" w="full">
          {!editor.isSectionEditing && !blankSlate ? (
            <HStack
              justify="flex-end"
              w="full"
              p={0}
              // Shown on hover or focus of the section; unsaved changes keep
              // it in view.
              css={
                form.dirty
                  ? undefined
                  : coverPageRevealOnHoverCss(COVER_PAGE_REVEAL_IN_SECTION)
              }
            >
              <CoverPageSectionEditTriggerButton
                label={triggerLabel}
                ariaLabel={
                  form.dirty
                    ? t("inline_section_editor.unsaved_trigger_label", {
                        defaultValue: "{{label}} (unsaved changes)",
                        label: triggerLabel
                      })
                    : t("panel_sections.edit_named", {
                        defaultValue: "Edit {{name}}",
                        name: triggerLabel
                      })
                }
                onClick={editor.startEditing}
                isDirty={form.dirty}
              />
            </HStack>
          ) : null}

          {!editor.isSectionEditing && blankSlate ? (
            blankSlate
          ) : (
            <InlineEditorField
              isEditing={editor.isSectionEditing}
              editRequestCount={editor.editRequestCount}
              isDirty={form.dirty}
              isSubmitting={form.isSubmitting}
              onDiscard={(fromKeyboard) => {
                editor.finishEditing()
                form.resetForm()
                void form.validateForm(form.initialValues)
                if (fromKeyboard) restoreFocus()
              }}
              onKeyDown={(event) => {
                if (!editor.isSectionEditing) return
                // Cmd/Ctrl+Enter applies, as in the panel forms.
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault()
                  keyboardSubmit.current = true
                  void form.submitForm().catch(() => undefined)
                } else if (
                  // Apply pressed with Enter or Space.
                  (event.key === "Enter" || event.key === " ") &&
                  event.target instanceof HTMLButtonElement
                ) {
                  keyboardSubmit.current = true
                }
              }}
            >
              <RichTextEditorWithMediaManager
                value={
                  editor.isSectionEditing ? form.values.value ?? "" : value
                }
                onChange={(value) => {
                  if (!editor.isSectionEditing) return
                  void form.setFieldValue("value", value, false)
                }}
                mediaManagerContextKey={mediaManagerContextKey}
                mode={editor.isSectionEditing ? "edit" : "view"}
                controlBar={{
                  showEditButton: false,
                  showSaveCancelButtons: false
                }}
                editorStyles={
                  editor.isSectionEditing
                    ? COVER_PAGE_INLINE_EDITOR_EDITING_STYLES
                    : COVER_PAGE_INLINE_EDITOR_SOFT_STYLES_SPACIOUS
                }
                placeholder={placeholder}
                bubbleToolbarPlacement="bottom"
                autoFocus
              />
              {editor.isSectionEditing ? (
                // The field's footer: the actions belong to the field, like a
                // comment composer. Sticky so they stay in reach under a long
                // draft.
                <HStack
                  justify="flex-end"
                  position="sticky"
                  bottom={0}
                  gap="5px"
                  px={2}
                  pb={2}
                >
                  {/* The panel's Apply button, so applying looks the same
                      wherever a Cover Page is edited. Leaving the field
                      discards; see the click-away handler. */}
                  <Button
                    {...AUGMENTATION_PANEL_ACTION_BUTTON_PROPS}
                    {...AUGMENTATION_PANEL_SUBMIT_BUTTON_PROPS}
                    px={4}
                    onClick={() =>
                      void form.submitForm().catch(() => undefined)
                    }
                    disabled={form.isSubmitting || !form.dirty}
                  >
                    {form.isSubmitting
                      ? t("panel.saving", { defaultValue: "Saving..." })
                      : t("panel.apply", { defaultValue: "Apply" })}
                  </Button>
                </HStack>
              ) : null}
            </InlineEditorField>
          )}
        </Box>
      )}
    </FormikWithDiscardFormState>
  )
}

export default CoverPageInlineSectionEditor
